import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Material UI
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Icons
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import ReplyIcon from '@mui/icons-material/Reply';
import BlockIcon from '@mui/icons-material/Block';
import PaymentIcon from '@mui/icons-material/Payment';
import LoopIcon from '@mui/icons-material/Loop';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import RepeatIcon from '@mui/icons-material/Repeat';

//Components
import RefundModal from './refund';
import NewSaleModal from './create';
import InvoicePrintModal from './print/invoice';
import ChangeTicketPrintModal from './print/changeTicket';
import Spinner from '../../components/spinner';
import VouchersComponent from '../../components/voucher/index';
import ModalSendEmail from '../../components/modal/sendEmail';
import TerminalQrPvModal from '../../components/modal/terminalQrPvModal';
import TerminalPvModal from '../../components/modal/terminalPvModal';

//Hooks
import useNotify from '../../hooks/notify';
import useOrder from '../../hooks/orders/useOrder';
import useAuthorize from '../../hooks/user/useAuthorize';
import useUser from '../../hooks/user/useUser';

// Models
import { OrderStates, CanInvoice, CanChange, CanChangeOtherBranch, CanCreateCI, CanPrint, CanRefund, CanVoid, CanContinuePayment, CanCancelPayment, CanRetryOperation, CanCancelOperation, HasPendingTerminalItems, onRedirectToRefundView } from '../../models/OrderEntity';
import { PaymentStates } from '../../models/PaymentStates';
import { Permissions, PermissionsSalesAuthorized } from '../../models/Roles';

// Others
import { subChannels } from '../../models/Channels';
import ModalManualInvoice from '../../components/modal/manualInvoice';
import HistoryModal from './history';
import { paymentMethods } from '../../models/PaymentMethods';
import ExtraDataModal from '../../components/modal/extraData';
import { getTerminalPvBlocker } from '../../utils/payment-utils';

const MenuActions = ({ order: selectedOrder, onChange = () => { } }) => {
    const isAuthorized = useAuthorize();
    const { business, branch, pv } = useUser();
    const push = useNavigate();
    const { Notify, variants } = useNotify();
    const { order, invoice, voidOrder, loading, paymentsCancelation, refund, retryOperation, cancelOperation, getCurrentOrder } = useOrder({ orderId: selectedOrder.id, selectedOrder })

    const [menuRef, setMenuRef] = useState(null);
    const [voucherSelected, setVoucherSelected] = useState(null);

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
    const [showChangeTicketPrintModal, setShowChangeTicketPrintModal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmData, setConfirmData] = useState({ message: '', onAccept: () => { }, onClose: () => { } });
    const [orderType, setOrderType] = useState(OrderStates.Refunded);
    const [refundData, setRefundData] = useState();
    const [manualInvoiceNumberData, setManualInvoiceNumberData] = useState();
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const [showManual, setShowManual] = useState(false);
    const [showRefundCupon, setShowRefundCupon] = useState(false);
    const [showExtraData, setShowExtraData] = useState(false);
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [sendEmailChange, setSendEmailChange] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);

    const initInvoice = async (manualInvoiceData = null, refundItems = null) => {
        try {
            const order = await invoice(manualInvoiceData, refundItems);
            if (order.state !== OrderStates.Invoiced)
                Notify("Ocurrio un error al intentar facturar.", variants.ERROR);
            else
                onChange(order);
        } catch (err) {
            String(err).includes("AfipException")
                ? Notify("Ocurrió un error al intentar facturar con AFIP, intente más tarde o facture manualmente", variants.ERROR)
                : Notify(err.message, variants.ERROR);
        }
    }

    const initRefund = async ({ refundItems, refundCupons, manualInvoiceData }) => {
        try {
            const order = await refund({ refundItems, refundCupons, manualInvoiceData });
            if (order.state !== OrderStates.Refunded && order.state !== OrderStates.PartiallyRefunded && order.state !== OrderStates.OperationProcessing)
                Notify("Ocurrio un error al intentar devolver.", variants.ERROR);
            else
                onChange(order);
        } catch (err) {
            Notify(err.message, variants.ERROR);
        }
    }

    const initVoid = async (extraData = {}) => {
        try {
            const order = await voidOrder(extraData);
            if (order.state !== OrderStates.Voided)
                Notify("Ocurrio un error al intentar realizar una devolucion total.", variants.ERROR);
            else
                onChange(order);
        } catch (err) {
            Notify(err.message, variants.ERROR);
        }
    }

    const continuePayment = () => {
        const payment = order.payments?.find(p => p.state === PaymentStates.Initialized)
        if (payment)
            window.location.href = payment.paymentUrl;
        else
            Notify("Hubo un problema al iniciar el pago.", variants.ERROR)

        setMenuRef(null);
    }

    const handleActionWithConfirm = (message, onAccept, onManual = null) => {
        setConfirmData({
            message,
            onAccept: () => {
                setShowConfirm(false);
                onAccept();
            },
            onManual: onManual
                ? () => {
                    setShowConfirm(false)
                    onManual();
                }
                : null,
            onClose: () => setShowConfirm(false),
        });

        setShowConfirm(true);
    };

    const handleChange = async () => {
        const currentOrder = await getCurrentOrder()

        // Los cambios sobre Terminal QR los rechaza sales-api por tipo de operación, así que acá
        // no se valida el PV del pago QR.
        const blocker = getTerminalPvBlocker(currentOrder, { pv, business, validateQrBillingCode: false })
        if (blocker) {
            setShowInfoModal(blocker);
            return
        }

        handleActionWithConfirm("¿Está seguro que desea realizar el cambio?", () => {
            setOrderType(OrderStates.Invoiced);
            setShowRefundModal(true);
        }
        );
    }

    const handleRefund = async () => {
        const currentOrder = await getCurrentOrder()

        // Las devoluciones parciales sobre Terminal QR las rechaza sales-api por tipo de operación.
        const blocker = getTerminalPvBlocker(currentOrder, { pv, business, validateQrBillingCode: false })
        if (blocker) {
            setShowInfoModal(blocker);
            return
        }

        handleActionWithConfirm("¿Está seguro que desea realizar una devolución?", () => {
            setOrderType(OrderStates.Refunded);
            setShowRefundModal(true);
        });
    }

    const handleVoid = async () => {
        const currentOrder = await getCurrentOrder()

        const blocker = getTerminalPvBlocker(currentOrder, { pv, business })
        if (blocker) {
            setShowInfoModal(blocker);
            return
        }

        const modalMessage = "¿Está seguro que desea realizar una devolución total?";
        if (currentOrder.state === OrderStates.PaymentProcessed) {
            handleActionWithConfirm(modalMessage, initVoid);
        } else
            handleActionWithConfirm(modalMessage, () => {
                setOrderType(OrderStates.Voided);
                setShowRefundModal(true);
            });
    }

    const handleInvoice = () => {
        handleActionWithConfirm("Se va a facturar la orden. ¿Confirma?", () => {
            if (order.itemsToReturn && order.itemsToReturn.length > 0) {
                setOrderType(OrderStates.InvoiceInitiated);
                setShowRefundModal(true);
            } else {
                setOrderType(null);
                if (!NeedExtraData())
                    initInvoice();
            }
        })

        setMenuRef(null);
    }

    const handleRefundSuccess = async (data, isManual, refundAmount) => {
        setShowRefundModal(false);
        setRefundData(data);

        switch (orderType) {
            case OrderStates.Refunded: // Devolucion Parcial
                if (!NeedExtraData(false, isManual, refundAmount))
                    await initRefund({ refundItems: data }); // Hacer Refund
                break;
            case OrderStates.Voided:
                if (!NeedExtraData(false, isManual, order.totalAmount))
                    initVoid({ refundItems: data });
                break;
            case OrderStates.Invoiced:
                if (!NeedExtraData(true)) {
                    setManualInvoiceNumberData(data.manualInvoiceData);
                    setShowModal(true);
                }
                break;
            case OrderStates.InvoiceInitiated:
                if (!NeedExtraData(false, isManual, order.totalAmount))
                    initInvoice(null, data);
                break;
            default:
                break;
        }
    };

    const cancelPayment = async () => {
        const orderCancel = await paymentsCancelation();
        if (orderCancel) {
            push(`/orders/${orderCancel.id}`);
        } else
            Notify('Hubo un problema al cancelar el pago.', variants.ERROR);

        setMenuRef(null);
    }

    const handlePrintInvoice = (voucher) => {
        setVoucherSelected(voucher);
        setShowInvoicePrintModal(true);
        setMenuRef(null);
    }

    const handlePrintChange = (voucher) => {
        setVoucherSelected(voucher);
        setSendEmailChange(false);
        setShowChangeTicketPrintModal(true);
        setMenuRef(null);
    }

    const handlePrintModalClose = () => {
        setVoucherSelected(null);
        setShowInvoicePrintModal(false);
        setShowSendEmailModal(false);
        setShowChangeTicketPrintModal(false);
    }

    const handleSendMail = (data) => {
        setVoucherSelected(data);
        setShowSendEmailModal(true);
    }

    const handleSendMailChange = (data) => {
        setSendEmailChange(true);
        setShowChangeTicketPrintModal(true);
        handlePrintModalClose();
        setShowSendEmailModal(true);
    }
    const refundCancel = () => {
        setShowRefundModal(false);
    }

    const handleInernalVoucher = () => {
        handleActionWithConfirm('Desea crear comprobante interno. ¿Confirma?',
            async () => {
                setShowConfirm(false);
                push(`/internalVouchers/create`, { order });
            });
    }

    const setManualInvoiceNumber = (data) => {
        setManualInvoiceNumberData(data);
    }

    const setRefund = (data) => {
        setRefundData(data);
    }

    const handleHistory = (item) => {
        setShowHistoryModal(true);
    }

    const NeedExtraData = (isChange, manualCheck = false, refundAmount = 0) => {
        let show = false;

        //Show manual invoice
        if (order.subChannel === subChannels.values.FacturaManual || manualCheck) {
            setShowManual(true);
            show = true;
        } else setShowManual(false);

        if (!isChange) {
            //show Cupon Refund
            const cardPayments = order.payments
                ?.filter(x => x.paymentMethodType === paymentMethods.Card || x.paymentMethodType === paymentMethods.Posnet)

            if (cardPayments.length <= 0)
                setShowRefundCupon(false);
            else if (cardPayments.length === 1 && order.payments.length === 1 && refundAmount > 0) {
                setShowRefundCupon(true);
                show = true;
            } else {
                //Si tiene otros pagos y el monto a devolver los supera
                const cashTotal = order.payments
                    ?.filter(x => x.paymentMethodType !== paymentMethods.Card && x.paymentMethodType !== paymentMethods.Posnet &&
                        (x.state === PaymentStates.Approved || x.state === PaymentStates.PartiallyRefunded))
                    .reduce((accumulator, cash) => accumulator + cash.totalAmount, 0) || 0;

                if (refundAmount > cashTotal) {
                    setShowRefundCupon(true);
                    show = true;
                }
            }
        }

        setShowExtraData(show); //Mostrar Popup
        return show;
    }

    const handleExtraData = async (data) => {
        setShowExtraData(false);

        switch (orderType) {
            case OrderStates.Refunded:
                await initRefund({ ...data, refundItems: refundData });
                break;
            case OrderStates.Invoiced:
                setManualInvoiceNumberData(data.manualInvoiceData);
                setShowModal(true);
                break;
            case OrderStates.Voided:
                initVoid({ ...data, refundItems: refundData });
                break;
            case OrderStates.InvoiceInitiated:
                initInvoice(data.manualInvoiceData, refundData);
                break;
            default:
                initInvoice(data.manualInvoiceData);
                break;
        }
    }

    const handleRetryOperation = async () => {
        handleActionWithConfirm("¿Desea reintentar la última operación?", async () => {
            const currentOrder = await retryOperation();

            if (!currentOrder) return

            onChange(currentOrder);
            setMenuRef(null);

            // El reintento republica el item en la terminal: hay que ir a resolverlo a payment-web.
            if (!HasPendingTerminalItems(currentOrder)) return

            const blocker = getTerminalPvBlocker(currentOrder, { pv, business })
            if (blocker) {
                setShowInfoModal(blocker);
                return
            }

            onRedirectToRefundView(currentOrder);
        });
    }

    const handleCancelOperation = async () => {
        handleActionWithConfirm("Se liberarán los items pendientes en la terminal integrada. Según lo que ya se haya procesado, la venta podrá restaurarse o la devolución quedará pendiente de reintento. ¿Confirma?", async () => {
            const currentOrder = await cancelOperation();

            // cancelOperation notifica el error y devuelve undefined si falló.
            if (!currentOrder) return

            if (currentOrder.state === OrderStates.PaymentProcessed)
                Notify("Se liberaron los items de la terminal integrada y la venta se restauró. No quedó nada pendiente.", variants.SUCCESS);
            else if (currentOrder.state === OrderStates.OperationRejected)
                Notify("Se liberaron los items de la terminal integrada, pero la devolución quedó interrumpida. Reintentá la operación desde la orden.", variants.WARNING);
            else
                Notify("Se liberaron los items pendientes en la terminal integrada.", variants.INFO);

            onChange(currentOrder);
            setMenuRef(null);
        });
    }

    //Action Menu
    return (
        <>
            {(loading || orderLoading) && <Spinner lockScreen message='Procesando orden' />}
            {showInfoModal && showInfoModal === "InvalidPvForTerminalQr" &&
                <TerminalQrPvModal
                    open={showInfoModal}
                    billingCode={order.billingCode}
                    branchCode={order.branchCode}
                    onAccept={() => setShowInfoModal(false)}
                />
            }
            {showInfoModal && showInfoModal === "InvalidPvForTerminal" &&
                <TerminalPvModal
                    open={showInfoModal}
                    onAccept={() => setShowInfoModal(false)}
                />
            }
            {showExtraData &&
                <ExtraDataModal
                    open={showExtraData}
                    onSave={handleExtraData}
                    onClose={() => setShowExtraData(false)}
                    order={order}
                    showManual={showManual}
                    showRefundCupon={showRefundCupon}
                    showPv
                    showCreditNote={order.refund && order.refund > 0 && order.vouchers.length === 0}
                />
            }
            {showModal &&
                <NewSaleModal
                    open={showModal}
                    orderId={order.id}
                    onClose={() => setShowModal(false)}
                    refundItems={refundData}
                    manualInvoiceNumber={manualInvoiceNumberData}
                />
            }
            {showHistoryModal &&
                <HistoryModal
                    open={showHistoryModal}
                    orderId={order.id}
                    onClose={() => setShowHistoryModal(false)}
                />
            }
            {((showRefundModal && order.round === 0) || (showRefundModal && (orderType === OrderStates.InvoiceInitiated || orderType === OrderStates.Voided))) &&
                <RefundModal
                    open={showRefundModal}
                    onSucces={handleRefundSuccess}
                    onClose={refundCancel}
                    order={order}
                    openMode={orderType}
                    setRefund={setRefund}
                    setManualInvoiceNumber={setManualInvoiceNumber}
                />
            }
            {showInvoicePrintModal &&
                <InvoicePrintModal
                    open={showInvoicePrintModal}
                    onClose={handlePrintModalClose}
                    voucher={voucherSelected}
                />
            }
            {showChangeTicketPrintModal &&
                order.items.filter(i => i.quantity - i.refundedQuantity > 0).length > 0 &&
                <ChangeTicketPrintModal
                    open={showChangeTicketPrintModal}
                    onClose={handlePrintModalClose}
                    voucher={voucherSelected}
                    items={order.items}
                    isSendMail={sendEmailChange}
                    customerEmail={order.customer?.email}
                />
            }
            {showConfirm &&
                <ModalManualInvoice
                    open={confirmData}
                    message={confirmData.message}
                    onAccept={confirmData.onAccept}
                    onManual={confirmData.onManual}
                    onCancel={confirmData.onClose}
                    onClose={confirmData.onClose}
                />
            }
            {showSendEmailModal &&
                <ModalSendEmail
                    title='Envio de mail'
                    open={showSendEmailModal}
                    onClose={handlePrintModalClose}
                    contentProps={{ dividers: true }}
                    email={order.customer?.email}
                    country={branch.country}
                    voucherId={voucherSelected?.id}
                    onCancel={handlePrintModalClose}
                />
            }
            <IconButton
                onClick={(e) => setMenuRef(e.currentTarget)}
                id="moreVerIcon"
                size="large"
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                id="action-menu"
                keepMounted
                anchorEl={menuRef}
                open={Boolean(menuRef)}
                onClose={() => setMenuRef(null)}
            >
                {isAuthorized([Permissions.SalesPayment]) && CanContinuePayment(order, branch.code) &&
                    <MenuItem onClick={continuePayment}>
                        <ListItemIcon>
                            <PaymentIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Continuar pago" />
                    </MenuItem>

                }
                {isAuthorized([Permissions.SalesPayment]) && CanCancelPayment(order, branch.code) &&
                    <MenuItem onClick={cancelPayment} id="canceledPayment">
                        <ListItemIcon>
                            <CloseIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Cancelar Pago" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesInvoince]) && CanInvoice(order, branch.code) &&
                    <MenuItem onClick={handleInvoice}>
                        <ListItemIcon>
                            <ReceiptIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Facturar" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesPrint]) && CanPrint(order, branch.code) &&
                    <VouchersComponent
                        vouchers={order.vouchers}
                        handle={handlePrintInvoice}
                        handleIsChange={handlePrintChange}
                        tooltip="Imprimir"
                        icon={
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                        }
                        keys="print"
                    />
                }
                {isAuthorized([Permissions.SalesPrint]) && CanPrint(order, branch.code) &&
                    <VouchersComponent
                        vouchers={order.vouchers}
                        handle={handleSendMail}
                        handleIsChange={handleSendMailChange}
                        tooltip="Enviar email"
                        icon={
                            <ListItemIcon>
                                <EmailIcon fontSize="small" />
                            </ListItemIcon>
                        }
                        keys="sendMail"
                    />
                }
                {isAuthorized([Permissions.SalesEdit]) && CanChange(order, branch.code) &&
                    <MenuItem onClick={handleChange}>
                        <ListItemIcon>
                            <LoopIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Realizar Cambio" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesRefund]) && CanRefund(order, branch.code) &&
                    <MenuItem onClick={handleRefund}>
                        <ListItemIcon>
                            <ReplyIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Devolución parcial" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesVoid]) && CanVoid(order, branch) &&
                    <MenuItem onClick={handleVoid}>
                        <ListItemIcon>
                            <BlockIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Devolución total" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.ComprobanteInter]) && CanCreateCI(order, branch.code) &&
                    <MenuItem onClick={handleInernalVoucher}>
                        <ListItemIcon>
                            <DescriptionIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Comprobante Interno" />
                    </MenuItem>
                }
                {isAuthorized(PermissionsSalesAuthorized) && CanChangeOtherBranch(order, branch) &&
                    <MenuItem onClick={handleChange}>
                        <ListItemIcon>
                            <LoopIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Cambio de otra sucursal" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesRefund]) && CanRetryOperation(order, branch.code) &&
                    <MenuItem onClick={handleRetryOperation}>
                        <ListItemIcon>
                            <RepeatIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Reintentar operación" />
                    </MenuItem>
                }
                {isAuthorized([Permissions.SalesRefund]) && CanCancelOperation(order, branch.code) &&
                    <MenuItem onClick={handleCancelOperation}>
                        <ListItemIcon>
                            <RepeatIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Cancelar operación" />
                    </MenuItem>
                }
                <MenuItem onClick={handleHistory}>
                    <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Historial" />
                </MenuItem>
            </Menu>
        </>
    );
}

export default MenuActions;